import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import StudentLayout from '@/components/layouts/StudentLayout';
import { 
  ArrowLeft, Clock, CheckCircle2, XCircle, 
  Loader2, Send, Award, Brain, MessageSquare
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const ExamTakingPage = () => {
  const { paperId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { api } = useAuth();

  // Detect admin preview mode
  const isAdminPreview = location.pathname.startsWith('/admin/preview');
  const backPath = isAdminPreview ? '/admin/preview/exams' : '/student/exams';
  
  const [paper, setPaper] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [timeStarted] = useState(Date.now());
  const [timeRemaining, setTimeRemaining] = useState(null);

  useEffect(() => {
    const fetchPaper = async () => {
      try {
        const response = await api.get(`/v2/exams/paper/${paperId}`);
        setPaper(response.data.paper);
        setQuestions(response.data.questions);
        if (response.data.paper?.duration) {
          setTimeRemaining(response.data.paper.duration * 60);
        }
      } catch (error) {
        console.error('Error fetching paper:', error);
        toast.error('Failed to load exam paper');
        navigate(backPath);
      } finally {
        setLoading(false);
      }
    };
    fetchPaper();
  }, [paperId, api, navigate, backPath]);

  // Timer countdown
  useEffect(() => {
    if (timeRemaining === null || results) return;
    
    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [timeRemaining, results]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleSubmit = async () => {
    if (submitting) return;
    
    setSubmitting(true);
    const timeTaken = Math.floor((Date.now() - timeStarted) / 1000);
    
    try {
      const response = await api.post(`/v2/exams/submit/${paperId}`, {
        answers,
        time_taken_seconds: timeTaken
      });
      setResults(response.data);
      toast.success('Exam submitted successfully!');
    } catch (error) {
      console.error('Error submitting exam:', error);
      toast.error('Failed to submit exam');
    } finally {
      setSubmitting(false);
    }
  };

  const currentQ = questions[currentQuestion];
  const isLastQuestion = currentQuestion === questions.length - 1;
  const answeredCount = Object.keys(answers).filter(k => answers[k]).length;

  if (loading) {
    return (
      <StudentLayout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      </StudentLayout>
    );
  }

  if (results) {
    return (
      <StudentLayout>
        <div data-testid="exam-results" className="max-w-3xl mx-auto">
          <div className="student-card bg-emerald-100 border-emerald-300 mb-8">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-emerald-500 rounded-2xl border-2 border-gray-800 flex items-center justify-center">
                <Award className="w-10 h-10 text-white" />
              </div>
              <div>
                <h1 className="font-heading text-3xl font-bold text-slate-800">Exam Complete!</h1>
                <p className="text-slate-600">Well done for completing {paper?.name}</p>
              </div>
            </div>
          </div>

          {/* Score Summary */}
          <div className="student-card mb-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div className="p-4 bg-purple-100 rounded-xl">
                <div className="text-3xl font-bold text-purple-600">
                  {results.score_percentage >= 90 ? 'A*' : 
                   results.score_percentage >= 80 ? 'A' : 
                   results.score_percentage >= 70 ? 'B' : 
                   results.score_percentage >= 60 ? 'C' : 
                   results.score_percentage >= 50 ? 'D' : 
                   results.score_percentage >= 40 ? 'E' : 'U'}
                </div>
                <div className="text-sm text-slate-600">Grade</div>
              </div>
              <div className="p-4 bg-emerald-100 rounded-xl">
                <div className="text-3xl font-bold text-emerald-600">{results.score_percentage}%</div>
                <div className="text-sm text-slate-600">Score</div>
              </div>
              <div className="p-4 bg-amber-100 rounded-xl">
                <div className="text-3xl font-bold text-amber-600">{results.marks_achieved}/{results.total_marks}</div>
                <div className="text-sm text-slate-600">Marks</div>
              </div>
              <div className="p-4 bg-pink-100 rounded-xl">
                <div className="text-3xl font-bold text-pink-600">+{results.points_earned || 0}</div>
                <div className="text-sm text-slate-600">Points</div>
              </div>
            </div>
            
            {results.questions?.some(q => q.ai_marked) && (
              <div className="mt-4 flex items-center gap-2 text-purple-600 bg-purple-50 px-4 py-2 rounded-lg">
                <Brain className="w-5 h-5" />
                <span className="text-sm font-medium">Extended answers marked by AI</span>
              </div>
            )}
          </div>

          {/* Question Results */}
          <div className="student-card">
            <h2 className="font-heading text-xl font-bold text-slate-800 mb-4">Question Breakdown</h2>
            <div className="space-y-4">
              {(results.questions || []).map((r, idx) => {
                return (
                  <div key={r.id || idx} className={`p-4 rounded-xl border-2 ${r.is_correct ? 'bg-emerald-50 border-emerald-300' : 'bg-red-50 border-red-300'}`}>
                    <div className="flex items-start gap-3">
                      {r.is_correct ? (
                        <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-1" />
                      ) : (
                        <XCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" />
                      )}
                      <div className="flex-1">
                        <p className="font-medium text-slate-800">Q{idx + 1}: {r.question}</p>
                        <div className="mt-2 text-sm">
                          <p className="text-slate-600">Your answer: <span className="font-medium">{r.user_answer || 'Not answered'}</span></p>
                          {!r.is_correct && r.answer && (
                            <p className="text-emerald-600">Correct answer: <span className="font-medium">{r.answer}</span></p>
                          )}
                          <p className="text-slate-500 mt-1">Marks: {r.marks_given}/{r.marks}</p>
                        </div>
                        
                        {r.feedback && (
                          <div className="mt-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                            <div className="flex items-center gap-2 text-purple-700 mb-1">
                              <MessageSquare className="w-4 h-4" />
                              <span className="text-sm font-semibold">{r.ai_marked ? 'AI Feedback' : 'Feedback'}</span>
                            </div>
                            <p className="text-sm text-purple-800">{r.feedback}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mt-6 flex justify-center">
            <Button onClick={() => navigate(backPath)} className="student-btn">
              Back to Exams
            </Button>
          </div>
        </div>
      </StudentLayout>
    );
  }

  return (
    <StudentLayout>
      <div data-testid="exam-taking" className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button 
            onClick={() => navigate(backPath)}
            className="flex items-center gap-2 text-slate-600 hover:text-emerald-600 font-medium"
          >
            <ArrowLeft className="w-5 h-5" />
            Exit Exam
          </button>
          
          {timeRemaining !== null && (
            <div className={`flex items-center gap-2 px-4 py-2 rounded-full font-bold ${timeRemaining < 300 ? 'bg-red-100 text-red-600' : 'bg-emerald-100 text-emerald-600'}`}>
              <Clock className="w-5 h-5" />
              {formatTime(timeRemaining)}
            </div>
          )}
        </div>

        {/* Paper Info */}
        <div className="student-card bg-emerald-100 border-emerald-300 mb-6">
          <h1 className="font-heading text-2xl font-bold text-slate-800">{paper?.name}</h1>
          <p className="text-slate-600 mt-1">Question {currentQuestion + 1} of {questions.length} • {answeredCount} answered</p>
        </div>

        {/* Question */}
        {currentQ && (
          <div className="student-card mb-6">
            <div className="flex items-center justify-between mb-4">
              <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-semibold">
                {currentQ.marks} {currentQ.marks === 1 ? 'mark' : 'marks'}
              </span>
              {currentQ.type === 'extended' && (
                <span className="flex items-center gap-1 px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-semibold">
                  <Brain className="w-4 h-4" />
                  AI Marked
                </span>
              )}
            </div>
            
            <h2 className="text-xl font-bold text-slate-800 mb-6">{currentQ.question}</h2>
            
            {currentQ.type === 'multiple_choice' ? (
              <div className="space-y-3">
                {currentQ.options.map((option, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleAnswerChange(currentQ.id, idx.toString())}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                      answers[currentQ.id] === idx.toString()
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-gray-200 hover:border-emerald-300'
                    }`}
                  >
                    <span className="font-bold text-slate-500 mr-3">{String.fromCharCode(65 + idx)}.</span>
                    <span className="text-slate-800">{option}</span>
                  </button>
                ))}
              </div>
            ) : currentQ.type === 'extended' ? (
              <Textarea
                value={answers[currentQ.id] || ''}
                onChange={(e) => handleAnswerChange(currentQ.id, e.target.value)}
                placeholder="Write your extended answer here... Your answer will be marked by AI."
                className="min-h-[200px] resize-none"
                data-testid="extended-answer"
              />
            ) : (
              <input
                type="text"
                value={answers[currentQ.id] || ''}
                onChange={(e) => handleAnswerChange(currentQ.id, e.target.value)}
                placeholder="Type your answer..."
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-emerald-500 focus:outline-none text-lg"
                data-testid="short-answer"
              />
            )}
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => setCurrentQuestion(prev => Math.max(0, prev - 1))}
            disabled={currentQuestion === 0}
          >
            Previous
          </Button>
          
          {/* Question dots */}
          <div className="flex gap-2">
            {questions.slice(Math.max(0, currentQuestion - 3), currentQuestion + 4).map((q, idx) => {
              const actualIdx = Math.max(0, currentQuestion - 3) + idx;
              return (
                <button
                  key={q.id}
                  onClick={() => setCurrentQuestion(actualIdx)}
                  className={`w-8 h-8 rounded-full text-sm font-bold transition-all ${
                    actualIdx === currentQuestion
                      ? 'bg-emerald-500 text-white'
                      : answers[q.id]
                        ? 'bg-emerald-100 text-emerald-600'
                        : 'bg-gray-100 text-gray-400'
                  }`}
                >
                  {actualIdx + 1}
                </button>
              );
            })}
          </div>
          
          {isLastQuestion ? (
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="student-btn"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" />Submitting...</>
              ) : (
                <><Send className="w-4 h-4 mr-2" />Submit Exam</>
              )}
            </Button>
          ) : (
            <Button
              onClick={() => setCurrentQuestion(prev => Math.min(questions.length - 1, prev + 1))}
              className="student-btn"
            >
              Next
            </Button>
          )}
        </div>
      </div>
    </StudentLayout>
  );
};

export default ExamTakingPage;
